/**
 * 19 - Coastal Guns (10 §2): two casemated guns inside a walled battery, embrasures to the SOUTH - the way the
 * ranger comes - each served by a crew who fight from it. Concrete shrugs off everything but a time bomb, and
 * the only way to one is round the back: the rear doors are on the north side, where automated mini-bunkers
 * cover the ground (move faster than a crawl and they fire).
 *
 * The rear-door rule is the casemate's `weakSide` (sim/props.ts): a charge counts only when it is laid on the
 * north side of the casemate - anywhere else it does nothing, and the player is told where the door is. A
 * casemate's yaw is 0 (front south, door north), and the asserts keep standing room at each door, inside the
 * door's arc and inside a mini-bunker's reach.
 */

import { blastOpens, PROPS } from '../../sim/props';
import { SPECIALS } from '../../sim/tuning';
import { placeMiniBunker } from '../garrison';
import { checker, clutter, guardAtDoor, guardsAbout, insideRect, structure } from './helpers';
import { crew, dress, put } from './helpers3a';
import { checkTargets } from './helpers3aAsserts';
import type { ArchetypeSpec } from './types';

export const CASEMATES = 2;
export const bunkersFor = (threat: number): number => (threat <= 5 ? 2 : threat <= 7 ? 3 : 4);

/** The rear door: the two metres of ground against the middle of a casemate's north wall. */
export const rearDoor = (c: { x: number; z: number }): { x0: number; z0: number; x1: number; z1: number } =>
  ({ x0: c.x - 2, z0: c.z - 5.5, x1: c.x + 2, z1: c.z - 3 });

export const coastalGuns: ArchetypeSpec = {
  id: 'coastal-guns',
  name: 'COASTAL GUNS',
  nativeBiome: 'temperate',
  baseThreat: 6,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wall',
  sealed: false,
  layout: 'compound',
  size: { w: [52, 58], d: [34, 34] },
  // North of the embrasures: neither gun may bear on the Osprey (validate/fairness.ts).
  pickupDz: [-10, -5],
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const cx = (g.rect.x0 + g.rect.x1) / 2;
    const z = Math.round((g.rect.z0 + g.rect.z1) / 2) + 1;
    const ids: number[] = [];
    const doors: Array<{ x: number; z: number }> = [];
    for (const side of [-1, 1]) {
      const casemate = put(g, 'casemate', cx + side * 12, z, { target: true, margin: 1 });
      ids.push(casemate.id);
      // The gun crew, behind the embrasure.
      crew(g, casemate, 0, 1.2);
      doors.push({ x: casemate.x, z: casemate.z - 3 });
    }
    // The mini-bunkers cover the doors: the first two one each, the rest between them and the gates.
    const wanted = bunkersFor(g.threat);
    let sited = 0;
    for (let tries = 0; tries < 60 && sited < wanted; tries++) {
      const door = doors[tries % doors.length];
      if (!door) break;
      const x = door.x + g.rng.range(-5, 5);
      const zz = door.z - g.rng.range(5, 9);
      if (insideRect(g.inner, x, zz) && placeMiniBunker(g.map, x, zz, 1)) sited++;
    }
    if (sited < wanted) g.map.problems.push('coastal-guns: no room for the mini-bunkers behind the casemates');

    // The shell store between the guns, the fire-control tower behind it.
    dress(g, 'magazine', cx, z + 1, { margin: 1.2 });
    dress(g, 'watchtower', cx, g.inner.z0 + 3, { margin: 1.2 });
    const quarters = structure(g, 'hut', { margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    if (quarters) guardAtDoor(g, quarters);
    guardsAbout(g, 1 + g.rng.int(2));
    return {
      objective: {
        kind: 'destroyTargets', label: 'DESTROY THE GUNS', targetPropIds: ids, minFraction: 1,
        rules: [{ type: 'recall', mode: 'normal' }],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('coastal-guns');
    const casemates = checkTargets(check, def, ['casemate']);
    check(casemates.length === CASEMATES, `needs ${CASEMATES} casemates, has ${casemates.length}`);
    check(def.objective.kind === 'destroyTargets' && def.objective.minFraction === 1, 'both guns must be destroyed');
    check(PROPS.casemate.durability === 'bombOnly', 'a casemate opens to a time bomb only');
    check(PROPS.casemate.weakSide === 'north', 'a casemate opens only from its rear door, in its north face');
    const bunkers = def.spawns.filter((s) => s.kind === 'miniBunker' && insideRect(def.goalZone, s.x, s.z));
    check(bunkers.length >= bunkersFor(def.threat), `needs ${bunkersFor(def.threat)} mini-bunkers in the battery`);
    check(bunkers.every((b) => b.dormantUntil === undefined), 'the mini-bunkers are live from the start');
    for (const c of casemates) {
      check(insideRect(def.goalZone, c.x, c.z), `casemate ${c.id} stands outside the battery`);
      // Embrasure south (yaw 0), crew watching that arc: the front is the wrong way in.
      check(c.yaw === 0, `casemate ${c.id} must face south`);
      const crews = def.spawns.filter((s) => s.mans === c.id);
      check(crews.length === 1 && Math.abs(crews[0]?.facing ?? 9) < 0.01, `casemate ${c.id} needs a crew facing south`);
      // The rear-door rule: room to stand at the door, where a charge counts (and one at the embrasure does not),
      // under a mini-bunker's detector.
      const door = rearDoor(c);
      check(probe.standable(door) >= 3, `no room to stand at the rear door of casemate ${c.id}`);
      const counts = [door.x0, door.x1].every((x) => blastOpens(c, x, door.z0) && blastOpens(c, x, door.z1));
      check(counts && !blastOpens(c, c.x, c.z + 3.5), `a charge must count at the rear door of casemate ${c.id} only`);
      const covered = bunkers.some((b) => b.z < c.z && (b.x - c.x) * (b.x - c.x) + (b.z - (c.z - 3)) * (b.z - (c.z - 3))
        <= SPECIALS.bunkerRadius * SPECIALS.bunkerRadius);
      check(covered, `no mini-bunker covers the rear of casemate ${c.id}`);
    }
    check(casemates.every((c) => def.pickup.z < c.z), 'the pickup must lie behind the embrasures');
    return out;
  },
};
